/**
 * Upload a file (image or document) to Cloudinary.
 * Uses the `auto` resource type so images, PDFs, and other documents are
 * all handled by a single endpoint without needing separate upload presets.
 *
 * @param {File} file - The file to upload
 * @param {Object} options - Upload options
 * @param {string} options.folder - Base folder path (e.g., 'diagnoses', 'pets')
 * @param {string} options.clinicId - Clinic ID for organization (will create clinic-specific folder)
 * @returns {Promise<string>} - The secure URL of the uploaded file
 */
export const uploadToCloudinary = async (file, options = {}) => {
  const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'your-cloud-name';
  const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'your-upload-preset';

  if (!cloudName || cloudName === 'your-cloud-name') {
    throw new Error('Cloudinary cloud name is not configured. Please set REACT_APP_CLOUDINARY_CLOUD_NAME environment variable.');
  }

  if (!uploadPreset || uploadPreset === 'your-upload-preset') {
    throw new Error('Cloudinary upload preset is not configured. Please set REACT_APP_CLOUDINARY_UPLOAD_PRESET environment variable.');
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', uploadPreset);

  // Build folder path: clinics/{clinic_id}/{folder}
  let folderPath = '';
  if (options.clinicId && options.folder) {
    folderPath = `clinics/${options.clinicId}/${options.folder}`;
  } else if (options.clinicId) {
    folderPath = `clinics/${options.clinicId}`;
  } else if (options.folder) {
    folderPath = options.folder;
  }

  if (folderPath) {
    formData.append('folder', folderPath);
  }

  // Use `auto` resource type: Cloudinary auto-detects images, PDFs, and raw files.
  const resourceType = 'auto';

  try {
    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${cloudName}/${resourceType}/upload`,
      {
        method: 'POST',
        body: formData,
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to upload file to Cloudinary');
    }

    const data = await response.json();
    return data.secure_url;
  } catch (error) {
    console.error('Cloudinary upload error:', error);
    throw error;
  }
};

/**
 * Delete image from Cloudinary (requires authenticated backend endpoint)
 * @param {string} publicId - The public ID of the image to delete
 * @returns {Promise<void>}
 */
export const deleteFromCloudinary = async (publicId) => {
  // This would typically be done from your backend for security
  // as it requires your API secret
  console.warn('Delete operation should be implemented on the backend');
  throw new Error('Delete operation not implemented on frontend for security reasons');
};

/**
 * Extract public ID from Cloudinary URL
 * @param {string} url - The Cloudinary URL
 * @returns {string|null} - The public ID or null if not a valid Cloudinary URL
 */
export const extractPublicId = (url) => {
  if (!url) return null;
  
  try {
    const urlParts = url.split('/');
    const uploadIndex = urlParts.indexOf('upload');
    if (uploadIndex === -1) return null;
    
    // Get everything after 'upload' and version (vXXXXXXXXXX)
    const pathParts = urlParts.slice(uploadIndex + 2);
    const filename = pathParts.join('/');
    
    // Remove file extension
    return filename.replace(/\.[^/.]+$/, '');
  } catch (error) {
    console.error('Error extracting public ID:', error);
    return null;
  }
};

/**
 * Helper function to upload clinic-specific images
 * Automatically retrieves clinic_id from current user
 * @param {File} file - The image file to upload
 * @param {string} category - Category folder (e.g., 'diagnoses', 'pets', 'owners')
 * @returns {Promise<string>} - The uploaded image URL
 */
export const uploadClinicImage = async (file, category) => {
  // Import getUser dynamically to avoid circular dependency
  const { getUser } = await import('./api');
  const user = getUser();
  
  if (!user?.clinic_id) {
    throw new Error('User clinic ID not found. Please log in again.');
  }
  
  return uploadToCloudinary(file, {
    clinicId: user.clinic_id,
    folder: category
  });
};
